import React, { useState, useCallback } from "react";
import { FormikProps } from "formik";
import {
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Popover,
	Select,
	SelectChangeEvent,
	AccordionDetails,
	AccordionSummary,
	FormHelperText,
} from "@mui/material";
import { DateRangePicker } from "mui-daterange-picker";
import type { DateRange } from "mui-daterange-picker";
import { PREFERRED_TIMEZONES } from "../../../../common/constants/timezones";
import { StyledAccordion } from "../../../pricing_list/components/TenderSection/StyledAccordion";

interface DateRangeSelectorProps {
	readonly timezone?: string;
	readonly setFieldValue: (field: string, value: unknown) => void;
	readonly dateRange?: DateRange;
	readonly formik: FormikProps<any>
}

export function DateRangeSelector({
	timezone,
	dateRange,
	setFieldValue,
	formik
}: DateRangeSelectorProps) {
	const [open, setOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	// Reorder timezones to have preferred ones first
	const preferredTimezones = PREFERRED_TIMEZONES;
	const allTimezones = Intl.supportedValuesOf("timeZone");
	const timezonesSet = new Set(preferredTimezones);
	for (const tz of allTimezones) {
		timezonesSet.add(tz);
	}
	const timezones: string[] = Array.from(timezonesSet);

	const handleDateRangeClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
		setOpen(true);
	};
	const handleDateRangeClose = () => {
		setOpen(false);
	};
	const handleDateRangeChange = (range: DateRange) => {
		setFieldValue("dateRange", range);
		setOpen(false);
	};
	const handleTimezoneChange = useCallback(
		(event: SelectChangeEvent<string>) => {
			setFieldValue("timezone", event.target.value);
		},
		[setFieldValue]
	);
	return (
		<Box>
			<StyledAccordion expanded={true}>
				<AccordionSummary>
					<span>Parameters</span>
				</AccordionSummary>
				<AccordionDetails>
					<FormControl
						{...(!!formik.errors.dateRange && formik.touched.dateRange) ? {sx: { "&.MuiFormControl-root": {
								borderLeft: "3px solid red"
							}}} : {}}
						style={{ paddingRight: "16px", minWidth: "200px" }} error={true}
					>
						<Button
							onClick={handleDateRangeClick}
							name="dateRangeButton"
							color="primary"
							variant="outlined"
							style={{
								padding: "16.5px 14px",
								color: "rgba(0, 0, 0, 0.87)", // matches MUI Select default text color
								borderColor: "rgba(0, 0, 0, 0.23)", // matches MUI Select outlined border
								boxSizing: "border-box",
								textTransform: "none",
							}}
							sx={{
								"&:hover": {
									borderColor: "rgba(0, 0, 0, 0.87)",
								},
							}}
						>
							{dateRange?.startDate && dateRange?.endDate
								? `${dateRange.startDate.toLocaleDateString()} - ${dateRange.endDate.toLocaleDateString()}`
								: "Select Date Range"}
						</Button>
						<Popover
							open={open}
							anchorEl={anchorEl}
							onClose={handleDateRangeClose}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "right",
							}}
						>
							<DateRangePicker
								open={open}
								toggle={handleDateRangeClose}
								onChange={handleDateRangeChange}
								initialDateRange={dateRange}
							/>
						</Popover>
					</FormControl>

					<FormControl>
						<InputLabel id="timezone-label">Timezone</InputLabel>
						<Select
							labelId="timezone-label"
							data-testid={"timezone-select"}
							value={timezone}
							label="Timezone"
							onChange={handleTimezoneChange}
							sx={{ minWidth: "200px" }}
						>
							{timezones.map((tz) => (
								<MenuItem key={tz} value={tz}>
									{tz}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</AccordionDetails>
			</StyledAccordion>
			{!!formik.errors.dateRange && formik.touched.dateRange && <FormHelperText sx={{ fontSize: "16px"}} error={true}>A date range is required</FormHelperText>}
		</Box>
	);
}
